import axios from 'axios';
import { BASE_URL } from './apiconfig';

/**
 * What the backend hands out for controlling a V1 / Coldfire spa over the legacy
 * Mosquitto broker: a JWT scoped to that one spa (its `sub` is the spa GUID, which the
 * broker's ACL ties to the `arctic/spa/{guid}/...` topics), how long it lasts, and the
 * broker's WebSocket URL for this brand and environment.
 */
export interface V1MqttTokenResponse {
  /** The GUID exactly as the legacy database stores it (often uppercase). */
  spa_guid: string;
  access_token: string;
  expires_in: number;
  broker_url: string | null;
}

export const fetchV1MqttToken = async (token: string | null, spaId: string): Promise<V1MqttTokenResponse> => {
  try {
    const { data } = await axios.post(
      `${BASE_URL}/spas/${encodeURIComponent(spaId)}/v1-mqtt-token`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return data.data;
  } catch (error) {
    console.log('Failed to get a V1 broker token. ', error);
    throw error;
  }
};

/**
 * A V1 / Coldfire spa's legacy database state. `channels` holds the broker's payload
 * shapes, so it runs through `V1_CHANNEL_TRANSFORM`; `extras` holds fields no broker
 * payload carries, already under their V2 keys.
 */
export interface V1Snapshot {
  connected: boolean;
  last_reported: string | null;
  channels: Record<string, unknown>;
  extras: {
    live: Record<string, any> | any[];
    settings: Record<string, any> | any[];
  };
}

export const fetchV1Snapshot = async (token: string | null, spaId: string): Promise<V1Snapshot> => {
  try {
    const { data } = await axios.get(
      `${BASE_URL}/spas/${encodeURIComponent(spaId)}/v1-snapshot`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return data.data;
  } catch (error) {
    console.log('Failed to get the V1 spa snapshot. ', error);
    throw error;
  }
};

/**
 * Where a V1 / Coldfire tub's clock arrives and whether it may be set. The tub answers a
 * clock request (or a set) on the classic web broker, not in the HTTP response.
 */
export interface V1ClockInfo {
  spa_guid: string;
  /** Classic's anonymous web broker; connect with the spa GUID as username. */
  web_broker_url: string | null;
  reply_topic: string;
  /** False for a Yoctub older than spa software 1.0.32. */
  can_set: boolean;
  /** A Yoctub keeps UTC; a Coldfire keeps local "spa time". */
  is_utc: boolean;
  /** A Coldfire reports a 0-based month; a Yoctub a 1-based one. */
  month_is_zero_based: boolean;
}

/** A wall-clock time: full year, 1-based month. */
export interface V1ClockTime {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const clockUrl = (spaId: string, suffix = '') => `${BASE_URL}/spas/${encodeURIComponent(spaId)}/v1-clock${suffix}`;

export const fetchV1ClockInfo = async (token: string | null, spaId: string): Promise<V1ClockInfo> => {
  const { data } = await axios.get(clockUrl(spaId), { headers: { Authorization: `Bearer ${token}` } });
  return data.data;
};

/** Ask the tub for its clock; the answer arrives on `reply_topic`. */
export const requestV1Clock = async (token: string | null, spaId: string): Promise<V1ClockInfo> => {
  const { data } = await axios.post(clockUrl(spaId, '/request'), {}, { headers: { Authorization: `Bearer ${token}` } });
  return data.data;
};

/** Set the tub's clock; the tub echoes its new time on `reply_topic`. */
export const setV1Clock = async (token: string | null, spaId: string, time: V1ClockTime): Promise<V1ClockInfo> => {
  const { data } = await axios.put(clockUrl(spaId), time, { headers: { Authorization: `Bearer ${token}` } });
  return data.data;
};
